import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/apiUrl';

const AdminSystemPage = () => {
  const [systemInfo, setSystemInfo] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [updateLog, setUpdateLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [schemaValidation, setSchemaValidation] = useState(null);
  const [health, setHealth] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);

  useEffect(() => {
    loadSystemInfo();
    loadHealth();
  }, []);

  const loadSystemInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/system/version', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemInfo(response.data);
    } catch (error) {
      console.error('Error loading system info:', error);
    }
  };

  const loadHealth = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/system/health', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHealth(response.data);
    } catch (error) {
      console.error('Error loading health:', error);
    }
  };

  const checkForUpdates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/system/check-updates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpdates(response.data.updates || []);

      if (response.data.updatesAvailable) {
        alert(`${response.data.updates.length} update(s) available!`);
      } else {
        alert('System is up to date!');
      }
    } catch (error) {
      console.error('Error checking updates:', error);
      alert('Failed to check for updates');
    }
    setLoading(false);
  };

  const sendTestEmail = async () => {
    if (!testEmailAddress) {
      alert('Enter a recipient email address first');
      return;
    }

    setTestingEmail(true);
    setTestEmailResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/system/test-email',
        { to: testEmailAddress },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestEmailResult(response.data);
    } catch (error) {
      console.error('Error sending test email:', error);
      setTestEmailResult({
        success: false,
        message: error.response?.data?.error || error.message
      });
    }
    setTestingEmail(false);
  };

  const validateSchema = async () => {
    setValidating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/system/validate-schema',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSchemaValidation(response.data);

      if (response.data.valid) {
        alert('✅ Schema validation passed! Safe to update.');
      } else {
        alert('⚠️ Schema validation found issues. Check details below.');
      }
    } catch (error) {
      console.error('Error validating schema:', error);
      alert('Failed to validate schema');
    }
    setValidating(false);
  };

  const createBackup = async () => {
    if (!window.confirm('Create database backup? This may take a few minutes.')) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/system/backup',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`✅ Backup created: ${response.data.backupFile}`);
    } catch (error) {
      console.error('Error creating backup:', error);
      alert('Failed to create backup');
    }
    setLoading(false);
  };

  const performUpdate = async () => {
    if (!window.confirm('Perform system update? The application will restart after update.')) return;

    setLoading(true);
    setUpdateLog([]);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/system/update',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUpdateLog(response.data.log || []);

      if (response.data.success) {
        alert('✅ Update completed! Restarting application...');
        setTimeout(() => {
          restartApplication();
        }, 2000);
      }
    } catch (error) {
      console.error('Error performing update:', error);
      setUpdateLog(error.response?.data?.log || []);
      alert('❌ Update failed! Check logs below.');
    }
    setLoading(false);
  };

  const restartApplication = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/system/restart',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Application restarting... Please wait 10 seconds and refresh the page.');

      setTimeout(() => {
        window.location.reload();
      }, 10000);
    } catch (error) {
      console.error('Error restarting:', error);
    }
  };

  const rollback = async (commitHash) => {
    if (!window.confirm(`Rollback to commit ${commitHash}? This will restore a previous version.`)) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/system/rollback',
        { commitHash },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('✅ Rollback successful! Restarting...');
      setTimeout(() => {
        restartApplication();
      }, 2000);
    } catch (error) {
      console.error('Error rolling back:', error);
      alert('Failed to rollback');
    }
    setLoading(false);
  };

  return (
    <div style={{ 
      padding: '2rem',
      paddingTop: '5rem',
      maxWidth: '1400px',
      margin: '0 auto',
      background: 'linear-gradient(135deg, #141517 0%, #1f2023 100%)',
      minHeight: '100vh'
    }}>
      <h1 style={{ 
        fontSize: '2.5rem',
        background: 'linear-gradient(135deg, #9c3f45 0%, #ea580c 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        marginBottom: '0.5rem'
      }}>
        🔧 System Management
      </h1>
      <p style={{ color: '#9ca3af', fontSize: '1.1rem', marginBottom: '2rem' }}>
        Automated updates and system monitoring
      </p>

      {/* System Info */}
      {systemInfo && (
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '2rem', 
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <h2 style={{ marginBottom: '1rem', color: '#9c3f45' }}>📊 Current System Status</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Version</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#9c3f45' }}>{systemInfo.version}</div>
            </div>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Branch</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ea580c' }}>{systemInfo.branch}</div>
            </div>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Commit</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', fontFamily: 'monospace', color: '#f97316' }}>{systemInfo.commit}</div>
            </div>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Last Update</div>
              <div style={{ fontSize: '1.2rem', color: '#9ca3af' }}>{systemInfo.lastUpdate}</div>
            </div>
          </div>
          
          {systemInfo.updatesAvailable && (
            <div style={{ 
              marginTop: '1rem', 
              padding: '1rem', 
              background: 'rgba(220, 38, 38, 0.1)', 
              border: '1px solid rgba(220, 38, 38, 0.3)',
              borderRadius: '8px'
            }}>
              <strong style={{ color: '#9c3f45' }}>⚡ {systemInfo.commitsAhead} update(s) available!</strong>
            </div>
          )}
        </div>
      )}

      {/* System Health */}
      {health && (
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '2rem', 
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <h2 style={{ marginBottom: '1rem', color: '#22c55e' }}>💚 System Health</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div style={{ padding: '1rem', background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '8px' }}>
              <h4 style={{ color: '#22c55e' }}>Database</h4>
              <div style={{ color: health.database.connected ? '#22c55e' : '#9c3f45', fontWeight: 'bold' }}>
                {health.database.connected ? '✅ Connected' : '❌ Disconnected'}
              </div>
            </div>
            <div style={{ padding: '1rem', background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: '8px' }}>
              <h4 style={{ color: '#eab308' }}>Memory</h4>
              <div style={{ color: '#9ca3af' }}>{health.memory.used} / {health.memory.total}</div>
              <div style={{ fontSize: '0.9rem', color: '#9ca3af' }}>Free: {health.memory.free}</div>
            </div>
            <div style={{ padding: '1rem', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px' }}>
              <h4 style={{ color: '#3b82f6' }}>Disk Space</h4>
              <div style={{ color: '#9ca3af' }}>{health.disk.used} / {health.disk.total}</div>
              <div style={{ fontSize: '0.9rem', color: '#9ca3af' }}>Available: {health.disk.available}</div>
            </div>
          </div>
        </div>
      )}

      {/* Test Email */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem'
      }}>
        <h2 style={{ marginBottom: '1rem', color: '#3b82f6' }}>📧 Test Email</h2>
        <p style={{ color: '#9ca3af', marginBottom: '1rem', fontSize: '0.9rem' }}>
          Sends a real test email using EMAIL_HOST/EMAIL_USER/EMAIL_PASSWORD from
          the server's .env — the same transporter the beta-approval and waitlist
          emails use — without needing to run through a full signup.
        </p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <input
            type="email"
            placeholder="recipient@example.com"
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
            style={{
              flex: '1 1 250px',
              padding: '0.75rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: 'white'
            }}
          />
          <button
            onClick={sendTestEmail}
            disabled={testingEmail}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'rgba(59, 130, 246, 0.2)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px',
              color: '#3b82f6',
              cursor: testingEmail ? 'not-allowed' : 'pointer',
              fontWeight: '500'
            }}
          >
            {testingEmail ? 'Sending...' : 'Send Test Email'}
          </button>
        </div>

        {testEmailResult && (
          <div style={{
            padding: '1rem',
            background: testEmailResult.success ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 38, 38, 0.1)',
            border: `1px solid ${testEmailResult.success ? 'rgba(34, 197, 94, 0.3)' : 'rgba(220, 38, 38, 0.3)'}`,
            borderRadius: '8px'
          }}>
            <div style={{ color: testEmailResult.success ? '#22c55e' : '#9c3f45', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {testEmailResult.success ? '✅ Sent' : '❌ Failed'}
            </div>
            <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: testEmailResult.config ? '0.75rem' : 0 }}>
              {testEmailResult.message}
            </div>
            {testEmailResult.config && (
              <div style={{ fontSize: '0.85rem', color: '#9ca3af', fontFamily: 'monospace' }}>
                <div>EMAIL_HOST: {testEmailResult.config.EMAIL_HOST || <span style={{ color: '#9c3f45' }}>not set</span>}</div>
                <div>EMAIL_PORT: {testEmailResult.config.EMAIL_PORT}</div>
                <div>EMAIL_SECURE: {testEmailResult.config.EMAIL_SECURE}</div>
                <div>EMAIL_USER: {testEmailResult.config.EMAIL_USER || <span style={{ color: '#9c3f45' }}>not set</span>}</div>
                <div>EMAIL_FROM: {testEmailResult.config.EMAIL_FROM}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Update Actions */}
      <div style={{ 
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '2rem', 
        borderRadius: '12px',
        marginBottom: '2rem'
      }}>
        <h2 style={{ marginBottom: '1rem', color: '#ea580c' }}>🚀 Update Management</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <button
            onClick={checkForUpdates}
            disabled={loading}
            style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, #9c3f45 0%, #b91c1c 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? '⏳ Checking...' : '🔍 Check for Updates'}
          </button>
          
          <button
            onClick={validateSchema}
            disabled={validating}
            style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: validating ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              opacity: validating ? 0.6 : 1
            }}
          >
            {validating ? '⏳ Validating...' : '✓ Validate Schema'}
          </button>
          
          <button
            onClick={createBackup}
            disabled={loading}
            style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? '⏳ Creating...' : '💾 Create Backup'}
          </button>
          
          <button
            onClick={performUpdate}
            disabled={loading || updates.length === 0}
            style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: (loading || updates.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              opacity: (loading || updates.length === 0) ? 0.6 : 1
            }}
          >
            {loading ? '⏳ Updating...' : '⬆️ Perform Update'}
          </button>
          
          <button
            onClick={restartApplication}
            disabled={loading}
            style={{
              padding: '1rem',
              background: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? '⏳ Restarting...' : '🔄 Restart App'}
          </button>
        </div>
      </div>

      {/* Available Updates */}
      {updates.length > 0 && (
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '2rem', 
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <h2 style={{ marginBottom: '1rem', color: '#9c3f45' }}>📦 Available Updates ({updates.length})</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {updates.map((update, index) => (
              <div 
                key={index}
                style={{
                  padding: '1rem',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.25rem', color: '#9ca3af' }}>{update.message}</div>
                  <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                    <span style={{ fontFamily: 'monospace', background: 'rgba(255, 255, 255, 0.1)', padding: '2px 6px', borderRadius: '4px', marginRight: '0.5rem' }}>
                      {update.hash}
                    </span>
                    by {update.author} • {update.date}
                  </div>
                </div>
                <button
                  onClick={() => rollback(update.hash)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Rollback to Here
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Schema Validation Results */}
      {schemaValidation && (
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '2rem', 
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <h2 style={{ marginBottom: '1rem', color: schemaValidation.valid ? '#22c55e' : '#9c3f45' }}>
            {schemaValidation.valid ? '✅ Schema Validation Passed' : '⚠️ Schema Validation Failed'}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {schemaValidation.validations.map((v, index) => (
              <div 
                key={index}
                style={{
                  padding: '0.75rem',
                  background: v.status === 'valid' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 38, 38, 0.1)',
                  border: `1px solid ${v.status === 'valid' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(220, 38, 38, 0.3)'}`,
                  borderRadius: '6px'
                }}
              >
                <strong style={{ color: v.status === 'valid' ? '#22c55e' : '#9c3f45' }}>{v.file}</strong>: <span style={{ color: '#9ca3af' }}>{v.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Update Log */}
      {updateLog.length > 0 && (
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '2rem', 
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <h2 style={{ marginBottom: '1rem', color: '#3b82f6' }}>📝 Update Log</h2>
          <div style={{ 
            background: '#000', 
            color: '#0f0', 
            padding: '1rem', 
            borderRadius: '6px',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {updateLog.map((log, index) => (
              <div key={index} style={{ marginBottom: '0.5rem' }}>
                <span style={{ color: log.status === 'success' ? '#0f0' : log.status === 'failed' ? '#f00' : '#ff0' }}>
                  [{log.step}]
                </span> {log.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div style={{ 
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        padding: '2rem', 
        borderRadius: '12px'
      }}>
        <h3 style={{ marginBottom: '1rem', color: '#3b82f6' }}>📚 Update Process</h3>
        <ol style={{ lineHeight: '1.8', color: '#9ca3af' }}>
          <li><strong>Check for Updates</strong> - Fetches latest changes from GitHub</li>
          <li><strong>Validate Schema</strong> - Tests database migrations in a safe transaction</li>
          <li><strong>Create Backup</strong> - Creates a database backup before updating</li>
          <li><strong>Perform Update</strong> - Pulls code, installs dependencies, builds frontend</li>
          <li><strong>Restart Application</strong> - Applies the update by restarting</li>
        </ol>
        <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(234, 179, 8, 0.1)', borderRadius: '6px', border: '1px solid rgba(234, 179, 8, 0.3)' }}>
          <strong style={{ color: '#eab308' }}>⚠️ Important:</strong> <span style={{ color: '#9ca3af' }}>Always create a backup before updating! Updates require a restart.</span>
        </div>
      </div>
    </div>
  );
};

export default AdminSystemPage;
